'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { StoreGallery } from '@eliadmin/core/src/api/general/general-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { CarouselWall } from '../ui/carousel-wall';

export function References({
  subtitle,
  title,
  galleries,
}: {
  subtitle: string;
  title: string;
  galleries: StoreGallery[];
}) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const options = {
    align: 'start' as const,
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <section
        id="referencie"
        className={cn('custom-section', 'relative py-8 sm:py-10 md:py-14 lg:py-[60px]')}
      >
        <div
          className={cn(
            'absolute right-0 top-0 z-20 hidden h-full w-full max-w-[350px] opacity-0 md:block',
            'ea-galleries__right-overlay',
          )}
        />
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-8 sm:flex-row sm:items-center sm:gap-6',
          )}
        >
          <div className="relative z-20 flex w-full max-w-[200px] flex-col items-start justify-start gap-8 lg:max-w-[384px]">
            <Heading
              subtitle={subtitle}
              title={title}
              tag="h2"
              className={{
                container: 'w-full max-w-[300px] gap-4 sm:max-w-[200px] lg:gap-6 xl:gap-8',
              }}
            />
            <CarouselControls
              className={{
                main: 'hidden sm:flex',
                button:
                  'h-12 w-12 rounded-full border-none bg-black text-white shadow-none hover:bg-black/75 hover:text-white',
              }}
            />
          </div>
          <div className="relative w-full md:max-w-[792px]">
            <CarouselWall className="hidden sm:block" />
            <Carousel
              options={options}
              className={{
                main: 'w-full overflow-visible',
                container: 'w-full gap-2 overflow-visible sm:gap-4 lg:gap-6',
                wrapper: 'w-full overflow-visible',
                slide:
                  'relative h-[323px] w-full max-w-[284px] overflow-hidden rounded-[24px] sm:h-[400px] sm:max-w-[300px] lg:h-[500px] lg:max-w-[384px]',
              }}
              slides={galleries.map(gallery => {
                const allImages = [gallery?.image, ...(gallery?.images || [])].filter(img => img);

                return (
                  <Image
                    onClick={handleOpenGallery(allImages, 0)}
                    key={gallery.name}
                    src={gallery?.image?.url}
                    alt={resolveImageAlt(gallery?.image, gallery?.name)}
                    width={0}
                    height={0}
                    className="absolute inset-0 z-0 h-full w-full cursor-pointer object-cover object-center transition-all hover:opacity-80"
                    sizes="100vw"
                  />
                );
              })}
            />
          </div>
          <CarouselControls
            className={{
              main: 'flex sm:hidden',
              button:
                'h-12 w-12 rounded-full border-none bg-black text-white shadow-none hover:bg-black/75 hover:text-white',
            }}
          />
        </div>
      </section>
    </>
  );
}
